// Data service for loading and randomizing dashboard data
import revenueData from '../data/revenue.json';
import operationsData from '../data/operations.json';
import advertisingData from '../data/advertising.json';
import trafficData from '../data/traffic.json';
import socialData from '../data/social.json';

// Utility functions for realistic randomization
const randomVariation = (value, percentage = 0.1) => {
  const variation = value * percentage;
  return Math.round(value + (Math.random() - 0.5) * 2 * variation);
};

const randomFloat = (value, percentage = 0.1, decimals = 2) => {
  const variation = value * percentage;
  return parseFloat((value + (Math.random() - 0.5) * 2 * variation).toFixed(decimals));
};

const randomGrowth = (min = -5, max = 25) => {
  return parseFloat((Math.random() * (max - min) + min).toFixed(1));
};

const randomPercentage = (base = 50, range = 20) => {
  return parseFloat((base + (Math.random() - 0.5) * range).toFixed(1));
};

// Apparel business specific product names
const apparelProducts = [
  'Azra Lilac Kalidaar', 'Shae Blockprinted Dhaka', 'Parisa Hot Pink Lehenga',
  'Amali Mint Kalidaar', 'Zara Embroidered Sharara', 'Noor Golden Anarkali',
  'Sana Velvet Lehenga', 'Farah Silk Gharara', 'Maya Chiffon Dupatta',
  'Layla Pearl Kalidaar', 'Isha Rose Gold Sharara', 'Kiran Ivory Lehenga'
];

const categories = ['Bridals', 'Formals', 'Casual', 'Accessories'];

// Randomize revenue data
const randomizeRevenue = (data) => {
  const randomized = JSON.parse(JSON.stringify(data));
  
  // Overview metrics
  randomized.overview.totalRevenue = randomVariation(data.overview.totalRevenue, 0.15);
  randomized.overview.revenueGrowth = randomGrowth(5, 30);
  randomized.overview.totalOrders = randomVariation(data.overview.totalOrders, 0.12);
  randomized.overview.orderGrowth = randomGrowth(8, 25);
  randomized.overview.averageOrderValue = randomVariation(data.overview.averageOrderValue, 0.08);
  randomized.overview.aovGrowth = randomGrowth(-2, 8);
  randomized.overview.conversionRate = randomFloat(data.overview.conversionRate, 0.15);
  randomized.overview.conversionGrowth = randomGrowth(-1, 5);
  randomized.overview.cartAbandonmentRate = randomPercentage(68, 10);
  randomized.overview.cartAbandonmentChange = randomGrowth(-8, 2);
  randomized.overview.activeCustomers = randomVariation(data.overview.activeCustomers, 0.1);
  randomized.overview.activeUsers = randomVariation(data.overview.activeUsers, 0.2);
  randomized.overview.currentOrders = randomVariation(data.overview.currentOrders, 0.3);
  
  // Channels
  randomized.channels = randomized.channels.map(channel => ({
    ...channel,
    revenue: randomVariation(channel.revenue, 0.12),
    orders: randomVariation(channel.orders, 0.15),
    aov: randomVariation(channel.aov, 0.08),
    growth: randomGrowth(-5, 35),
    conversion: randomFloat(channel.conversion, 0.2)
  }));
  
  // Customer Journey
  randomized.customerJourney = randomized.customerJourney.map(step => ({
    ...step,
    website: randomVariation(step.website, 0.15),
    mobile: randomVariation(step.mobile, 0.18),
    store: randomVariation(step.store, 0.12),
    social: randomVariation(step.social, 0.25)
  }));
  
  // Inventory
  randomized.inventory.totalSku = randomVariation(data.inventory.totalSku, 0.05);
  randomized.inventory.lowStock = randomVariation(data.inventory.lowStock, 0.3);
  randomized.inventory.outOfStock = randomVariation(data.inventory.outOfStock, 0.4);
  
  return randomized;
};

// Randomize operations data
const randomizeOperations = (data) => {
  const randomized = JSON.parse(JSON.stringify(data));
  
  // Overview metrics
  randomized.overview.ordersShippedYesterday = randomVariation(data.overview.ordersShippedYesterday, 0.4);
  randomized.overview.sentToProductionYesterday = randomVariation(data.overview.sentToProductionYesterday, 0.3);
  randomized.overview.mtdOrdersShipped = randomVariation(data.overview.mtdOrdersShipped, 0.2);
  randomized.overview.mtdOrdersSentToProduction = randomVariation(data.overview.mtdOrdersSentToProduction, 0.2);
  randomized.overview.ordersPastDue = randomVariation(data.overview.ordersPastDue, 0.25);
  randomized.overview.mtdTimelineCompliance = randomPercentage(38, 15);
  randomized.overview.ordersShippedOnTime = randomVariation(data.overview.ordersShippedOnTime, 0.3);
  randomized.overview.ordersShippedLate = randomVariation(data.overview.ordersShippedLate, 0.3);
  
  // Daily fulfillment
  randomized.dailyFulfillment = randomized.dailyFulfillment.map(day => ({
    ...day,
    shipped: randomVariation(day.shipped, 0.4),
    produced: randomVariation(day.produced, 0.3),
    backlog: randomVariation(day.backlog, 0.15)
  }));
  
  // API integrations - randomize response times and sync times
  randomized.apiIntegrations = randomized.apiIntegrations.map(api => {
    const statuses = ['Connected', 'Connected', 'Connected', 'Warning', 'Error'];
    const syncTimes = ['1 min ago', '2 min ago', '3 min ago', '5 min ago', '15 min ago', '1 hour ago'];
    
    return {
      ...api,
      status: Math.random() > 0.8 ? statuses[Math.floor(Math.random() * statuses.length)] : api.status,
      lastSync: Math.random() > 0.7 ? syncTimes[Math.floor(Math.random() * syncTimes.length)] : api.lastSync,
      responseTime: api.status === 'Connected' ? randomVariation(api.responseTime, 0.3) : api.responseTime
    };
  });
  
  return randomized;
};

// Randomize advertising data
const randomizeAdvertising = (data) => {
  const randomized = JSON.parse(JSON.stringify(data));
  
  // Overview metrics
  randomized.overview.spendYesterday = randomVariation(data.overview.spendYesterday, 0.2);
  randomized.overview.mtdTotalSpend = randomVariation(data.overview.mtdTotalSpend, 0.15);
  randomized.overview.netAdToSal = randomFloat(data.overview.netAdToSal, 0.15);
  randomized.overview.netROAS = randomFloat(data.overview.netROAS, 0.12);
  randomized.overview.costPerAcquisition = randomFloat(data.overview.costPerAcquisition, 0.18);
  randomized.overview.clickThroughRate = randomFloat(data.overview.clickThroughRate, 0.2);
  
  // Top products - randomize with apparel names
  randomized.topProducts = randomized.topProducts.map((product, index) => ({
    ...product,
    name: apparelProducts[index] || product.name,
    revenue: randomVariation(product.revenue, 0.15),
    orders: randomVariation(product.orders, 0.2),
    category: categories[Math.floor(Math.random() * categories.length)]
  }));
  
  // Campaign performance
  randomized.campaignPerformance = randomized.campaignPerformance.map(campaign => ({
    ...campaign,
    spend: randomVariation(campaign.spend, 0.15),
    revenue: randomVariation(campaign.revenue, 0.12),
    roas: randomFloat(campaign.roas, 0.1),
    ctr: randomFloat(campaign.ctr, 0.2),
    cpc: randomFloat(campaign.cpc, 0.15)
  }));
  
  return randomized;
};

// Randomize traffic data
const randomizeTraffic = (data) => {
  const randomized = JSON.parse(JSON.stringify(data));
  
  // Overview metrics
  randomized.overview.visitorsYesterday = randomVariation(data.overview.visitorsYesterday, 0.2);
  randomized.overview.mtdTotalVisitors = randomVariation(data.overview.mtdTotalVisitors, 0.15);
  randomized.overview.engagedSessions = randomPercentage(54, 8);
  randomized.overview.bounceRate = randomPercentage(46, 6);
  randomized.overview.organicVisitors = randomVariation(data.overview.organicVisitors, 0.18);
  randomized.overview.paidVisitors = randomVariation(data.overview.paidVisitors, 0.22);
  randomized.overview.sessionDuration = randomVariation(data.overview.sessionDuration, 0.15);
  randomized.overview.pagesPerSession = randomFloat(data.overview.pagesPerSession, 0.12);
  
  // Geographic distribution
  randomized.geographicDistribution = randomized.geographicDistribution.map(country => ({
    ...country,
    visitors: randomVariation(country.visitors, 0.15),
    sessions: randomVariation(country.sessions, 0.15),
    revenue: randomVariation(country.revenue, 0.18)
  }));
  
  return randomized;
};

// Randomize social data
const randomizeSocial = (data) => {
  const randomized = JSON.parse(JSON.stringify(data));
  
  // Overview metrics
  randomized.overview.postsCreatedYesterday = randomVariation(data.overview.postsCreatedYesterday, 0.5);
  randomized.overview.mtdTotalPosts = randomVariation(data.overview.mtdTotalPosts, 0.3);
  randomized.overview.mtdLikes = randomVariation(data.overview.mtdLikes, 0.2);
  randomized.overview.mtdEngagement = randomVariation(data.overview.mtdEngagement, 0.18);
  randomized.overview.mtdComments = randomVariation(data.overview.mtdComments, 0.25);
  randomized.overview.mtdShares = randomVariation(data.overview.mtdShares, 0.3);
  randomized.overview.engagementRate = randomFloat(data.overview.engagementRate, 0.15);
  randomized.overview.reach = randomVariation(data.overview.reach, 0.2);
  randomized.overview.impressions = randomVariation(data.overview.impressions, 0.18);
  randomized.overview.mtdSaves = randomVariation(data.overview.mtdSaves, 0.22);
  
  // Platform breakdown
  randomized.platformBreakdown = randomized.platformBreakdown.map(platform => ({
    ...platform,
    followers: randomVariation(platform.followers, 0.05),
    engagement: randomVariation(platform.engagement, 0.2),
    reach: randomVariation(platform.reach, 0.18),
    posts: randomVariation(platform.posts, 0.4)
  }));
  
  return randomized;
};

// Main data service
export const getDashboardData = () => {
  return {
    revenue: randomizeRevenue(revenueData),
    operations: randomizeOperations(operationsData),
    advertising: randomizeAdvertising(advertisingData),
    traffic: randomizeTraffic(trafficData),
    social: randomizeSocial(socialData)
  };
};

// Get static data (without randomization)
export const getStaticDashboardData = () => {
  return {
    revenue: revenueData,
    operations: operationsData,
    advertising: advertisingData,
    traffic: trafficData,
    social: socialData
  };
};

export default getDashboardData;